import type { DateFieldConstraints } from "@/types/api/formSchema";

// All arithmetic + formatting is UTC-based to avoid the same off-by-one that
// hit dateAdd in P1 (parses "YYYY-MM-DD" as UTC midnight, then local getters
// return the previous day on negative-UTC offsets). Callers exchange
// "YYYY-MM-DD" strings, not local Date objects, so UTC is the right frame.
export function startOfTodayUTC(): Date {
  const now = new Date();
  return new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  ));
}

export function formatDateISO(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const OFFSET_RE = /^([+-])(\d+)([dwmy])$/;

function applyOffset(base: Date, offset: string): Date {
  const match = OFFSET_RE.exec(offset);
  if (!match) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[dateConstraints] Unknown offset unit in "${offset}"; expected d|w|m|y`);
    }
    return base;
  }
  const sign = match[1] === "-" ? -1 : 1;
  const n = Number(match[2]) * sign;
  const unit = match[3];
  const result = new Date(base);
  switch (unit) {
    case "d":
      result.setUTCDate(result.getUTCDate() + n);
      break;
    case "w":
      result.setUTCDate(result.getUTCDate() + n * 7);
      break;
    case "m":
      result.setUTCMonth(result.getUTCMonth() + n);
      break;
    case "y":
      result.setUTCFullYear(result.getUTCFullYear() + n);
      break;
  }
  return result;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseISODate(value: string): Date | undefined {
  const [y, m, d] = value.split("-").map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return undefined;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (
    date.getUTCFullYear() !== y ||
    date.getUTCMonth() !== m - 1 ||
    date.getUTCDate() !== d
  )
    return undefined;
  return date;
}

export function resolveDateValue(
  value: string | null | undefined,
): Date | undefined {
  if (value == null) return undefined;

  const today = startOfTodayUTC();

  if (value === "today") return today;

  if (value === "tomorrow") {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() + 1);
    return d;
  }

  if (OFFSET_RE.test(value)) return applyOffset(today, value);

  if (ISO_DATE_RE.test(value)) return parseISODate(value) ?? undefined;

  return undefined;
}

export function resolveDateConstraints(
  constraints: DateFieldConstraints | undefined,
): {
  minDate: Date | undefined;
  maxDate: Date | undefined;
  disabledDays: number[] | undefined;
  disabledDates: Date[] | undefined;
} {
  if (!constraints) {
    return {
      minDate: undefined,
      maxDate: undefined,
      disabledDays: undefined,
      disabledDates: undefined,
    };
  }

  let minDate = resolveDateValue(constraints.min_date);
  let maxDate = resolveDateValue(constraints.max_date);

  if (minDate && maxDate && minDate > maxDate) {
    [minDate, maxDate] = [maxDate, minDate];
  }

  const disabledDays =
    constraints.disabled_days && constraints.disabled_days.length > 0
      ? constraints.disabled_days
      : undefined;

  const parsedDates =
    constraints.disabled_dates && constraints.disabled_dates.length > 0
      ? constraints.disabled_dates
          .map(parseISODate)
          .filter((d): d is Date => d != null)
      : undefined;
  const disabledDates = parsedDates?.length ? parsedDates : undefined;

  return { minDate, maxDate, disabledDays, disabledDates };
}

export function resolveDefaultValue(
  constraints: DateFieldConstraints | undefined,
  resolvedMinDate: Date | undefined,
): string | undefined {
  if (!constraints?.default_value) return undefined;

  const val = constraints.default_value;

  if (val === "min_date") {
    return resolvedMinDate ? formatDateISO(resolvedMinDate) : undefined;
  }

  const resolved = resolveDateValue(val);
  return resolved ? formatDateISO(resolved) : undefined;
}
